"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction } from "next-safe-action/hooks";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Linkedin, Mail } from "lucide-react";
import { PhoneInput } from "@/components/ui/phone-input";
import Link from "next/link";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import {
  startupApplicationSchema,
  type StartupApplicationFormValues,
  STARTUP_STAGES,
  SECTORS,
  DISCOVERY_SOURCES,
  CHALLENGE_CATEGORIES,
} from "@/features/auth/types/application-schemas";
import {
  submitStartupApplication,
  requestInviteCode,
} from "@/features/auth/actions/submit-application";

export function StartupApplicationForm() {
  const [submitted, setSubmitted] = useState(false);
  const [showRequestSection, setShowRequestSection] = useState(false);
  const [inviteCodeRequested, setInviteCodeRequested] = useState(false);

  const form = useForm<StartupApplicationFormValues>({
    resolver: zodResolver(startupApplicationSchema),
    defaultValues: {
      name: "",
      founderName: "",
      email: "",
      phone: "",
      spocLinkedin: "",
      inviteCode: "",
      companyName: "",
      startupDescription: "",
      stage: undefined,
      sector: undefined,
      website: "",
      challengeCategory: undefined,
      challengeOther: "",
      discoverySource: undefined,
      termsAccepted: false,
    },
    mode: "onBlur",
  });

  const { execute, status } = useAction(submitStartupApplication, {
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (args) => {
      const message =
        args.error.serverError ??
        "Couldn't submit your application. Please try again.";
      toast.error(message);
    },
  });

  const { execute: executeRequest, status: requestStatus } = useAction(
    requestInviteCode,
    {
      onSuccess: () => {
        setInviteCodeRequested(true);
      },
      onError: (args) => {
        const message =
          args.error.serverError ??
          "Couldn't submit your request. Please try again.";
        toast.error(message);
      },
    }
  );

  const isPending = status === "executing";
  const isRequestPending = requestStatus === "executing";

  // Watch fields for live feedback
  const challengeCategory = form.watch("challengeCategory");
  const startupDescription = form.watch("startupDescription");
  const descWordCount = startupDescription
    ? startupDescription.trim().split(/\s+/).filter(Boolean).length
    : 0;

  function onRequestInviteCode() {
    const founderName = form.getValues("founderName");
    const email = form.getValues("email");
    const spocLinkedin = form.getValues("spocLinkedin");

    if (!founderName || !email) {
      toast.error(
        "Please fill in your Founder Name (SPOC) and Email before requesting a code."
      );
      return;
    }

    executeRequest({
      name: founderName,
      founderName,
      email,
      spocLinkedin: spocLinkedin || undefined,
    });
  }

  function onSubmit(values: StartupApplicationFormValues) {
    // Compose the stored challenge string from the tile selection
    const challenge =
      values.challengeCategory === "Other"
        ? `Other: ${values.challengeOther?.trim()}`
        : values.challengeCategory;

    execute({
      name: values.founderName,
      founderName: values.founderName,
      email: values.email,
      phone: values.phone || undefined,
      spocLinkedin: values.spocLinkedin || undefined,
      inviteCode: values.inviteCode,
      companyName: values.companyName,
      startupDescription: values.startupDescription,
      stage: values.stage,
      sector: values.sector,
      website: values.website || undefined,
      challenge,
      discoverySource: values.discoverySource,
    });
  }

  // ── Invite Code Requested confirmation screen ─────────────────────────────
  if (inviteCodeRequested) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="flex flex-col items-center gap-6 py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-display font-bold text-foreground">
              Invite Code Requested!
            </h2>
            <p className="text-muted-foreground max-w-sm">
              We'll send an invite code to{" "}
              <span className="font-medium text-foreground">
                {form.getValues("email")}
              </span>{" "}
              shortly. Once you receive it, come back to this page and complete
              your application.
            </p>
          </div>
          <Link href="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  // ── Application submitted confirmation screen ─────────────────────────────
  if (submitted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="flex flex-col items-center gap-6 py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-display font-bold text-foreground">
              Application Submitted!
            </h2>
            <p className="text-muted-foreground max-w-sm">
              We'll review your application and respond within 2-3 business
              days. You'll receive an email with next steps.
            </p>
          </div>
          <Link href="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="pb-6">
        <CardTitle className="text-2xl font-display">Apply as Builder</CardTitle>
        <CardDescription>
          Join Purple Venture Studio as a Startup Founder
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Personal Information
              </h3>

              {/* Row 1: Founder Name (SPOC) */}
              <FormField
                control={form.control}
                name="founderName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Founder Name (SPOC) *</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane Smith" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Row 2: Email + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Row 3: SPOC LinkedIn (full width) */}
              <FormField
                control={form.control}
                name="spocLinkedin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn Profile (SPOC)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder="https://linkedin.com/in/your-profile"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Row 4: Invite Code (required) */}
              {/* Row 4: Invite Code (required) */}
              <FormField
                control={form.control}
                name="inviteCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invite Code *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. PVS-A3X9K2" {...field} />
                    </FormControl>
                    <FormMessage />
                    {!showRequestSection && (
                      <button
                        type="button"
                        onClick={() => setShowRequestSection(true)}
                        className="w-full mt-2 flex items-center justify-between gap-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/70 transition-all duration-200 px-4 py-3 group cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0 group-hover:bg-primary/25 transition-colors">
                            <Mail className="h-4 w-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-semibold text-primary">Don't have an invite code?</p>
                            <p className="text-xs text-muted-foreground">Request one and we'll send it to your email</p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full group-hover:bg-primary/20 transition-colors shrink-0">
                          Request Code →
                        </span>
                      </button>
                    )}
                    {showRequestSection && (
                      <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-primary shrink-0" />
                          <p className="text-sm font-semibold text-primary">Request an Invite Code</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Fill in your Founder Name and Email above, then click below to request a code. We'll review and send it to your inbox.
                        </p>
                        <div className="flex items-center gap-3">
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className="btn-premium text-white"
                            disabled={isRequestPending}
                            onClick={onRequestInviteCode}
                          >
                            {isRequestPending ? (
                              <>
                                <Spinner className="mr-2" />
                                Sending Request…
                              </>
                            ) : (
                              "Send Me an Invite Code"
                            )}
                          </Button>
                          <button
                            type="button"
                            onClick={() => setShowRequestSection(false)}
                            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <Separator />



            {/* Startup Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Startup Details
              </h3>
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Inc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Startup description */}
              <FormField
                control={form.control}
                name="startupDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Describe your startup / problem you are solving *
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="e.g. Small retailers in India lose 20–30% of perishable inventory due to poor demand forecasting. We build an AI-powered tool that predicts daily stock needs so they order exactly what they'll sell."
                        className="resize-none min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    {/* Helper row: guidance text + live word count */}
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Explain the problem you are solving, for whom, and how
                        your solution works — in 1–2 clear sentences.
                      </p>
                      <span
                        className={cn(
                          "text-xs shrink-0 tabular-nums",
                          descWordCount === 0
                            ? "text-muted-foreground"
                            : descWordCount < 20
                              ? "text-destructive"
                              : descWordCount > 80
                                ? "text-destructive"
                                : "text-muted-foreground"
                        )}
                      >
                        {descWordCount} / 80 words
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="stage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stage *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STARTUP_STAGES.map((stage) => (
                            <SelectItem key={stage} value={stage}>
                              {stage}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sector"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sector *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select sector" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SECTORS.map((sector) => (
                            <SelectItem key={sector} value={sector}>
                              {sector}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://yourcompany.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Current Challenge — category tile selector */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Your Challenge
              </h3>

              {/* Tile grid — single-select */}
              <FormField
                control={form.control}
                name="challengeCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      What's your biggest challenge right now? *
                    </FormLabel>
                    <FormControl>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                        {CHALLENGE_CATEGORIES.map((cat) => (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => field.onChange(cat.value)}
                            className={cn(
                              "flex flex-col items-start gap-2 rounded-lg border-2 p-3 text-left",
                              "transition-colors cursor-pointer",
                              "border-border bg-card hover:bg-accent hover:border-primary/50",
                              field.value === cat.value &&
                                "border-primary bg-primary/5"
                            )}
                          >
                            <span className="text-xl leading-none">
                              {cat.emoji}
                            </span>
                            <span className="text-sm font-medium leading-tight text-foreground">
                              {cat.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Conditional "Other" free-text input */}
              {challengeCategory === "Other" && (
                <FormField
                  control={form.control}
                  name="challengeOther"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Please briefly describe your challenge *
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Please briefly describe your challenge…"
                          maxLength={200}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <Separator />

            {/* Discovery */}
            <FormField
              control={form.control}
              name="discoverySource"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How did you hear about Purple?</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DISCOVERY_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Terms */}
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal cursor-pointer">
                      I agree to Purple Venture Studio's Terms &amp; Conditions,
                      including the no off-platform solicitation policy.
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full btn-premium text-white"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Spinner className="mr-2" />
                  Submitting...
                </>
              ) : (
                "Submit Application"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
